import React from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight } from "lucide-react";
import cn from "./cn";

/**
 * Breadcrumbs — where a page sits in its module, every step above it a link.
 *
 * `items` runs from the top of the hierarchy down to this page:
 * `[{ label, to, icon? }, …, { label }]`. The last step is where you are and is
 * not a link. The one before it is the page's parent — which is also where
 * PageHeader's back button goes, so the arrow and the trail never disagree.
 *
 * Steps are real links: they open in a new tab, and show where they go on hover.
 * A long name is cut to one line and kept whole in its tooltip.
 *
 * Under 768px the trail gives way to one link up to the parent. The top nav has
 * no room for the back arrow there, and up one step is the step people take.
 *
 * A trail of one step is the top of its module and draws nothing.
 */
const STEP_CLASSES =
  "tw-inline-flex tw-min-w-0 tw-items-center tw-gap-1.5 tw-rounded-sm tw-px-1.5 tw-py-1 tw-leading-5";

export default function Breadcrumbs({ items = [], className = "" }) {
  if (items.length < 2) return null;
  const parent = items[items.length - 2];

  return (
    <nav aria-label="Breadcrumb" className={cn("tw-min-w-0", className)}>
      <ol className="tw-m-0 tw-flex tw-min-w-0 tw-list-none tw-flex-wrap tw-items-center tw-gap-y-0.5 tw-p-0 tw-text-[0.8125rem] max-[767px]:tw-hidden">
        {items.map((item, i) => {
          const current = i === items.length - 1;
          const Icon = item.icon;
          const body = (
            <>
              {Icon && <Icon size={14} aria-hidden="true" className={cn("tw-shrink-0", i === 0 && "tw-text-primary")} />}
              <span className="tw-truncate">{item.label}</span>
            </>
          );
          return (
            <li key={item.to || `${i}-${item.label}`} className="tw-flex tw-min-w-0 tw-items-center">
              {i > 0 && (
                <ChevronRight size={14} aria-hidden="true" className="tw-mx-0.5 tw-shrink-0 tw-text-content-subtle" />
              )}
              {current ? (
                <span
                  aria-current="page"
                  title={item.label}
                  className={cn(STEP_CLASSES, "tw-max-w-[20rem] tw-font-semibold tw-text-content")}
                >
                  {body}
                </span>
              ) : (
                <Link
                  to={item.to}
                  title={item.label}
                  className={cn(
                    STEP_CLASSES,
                    "tw-max-w-[14rem] tw-font-medium tw-text-content-muted tw-no-underline tw-transition-colors",
                    "hover:tw-bg-muted hover:tw-text-content hover:tw-no-underline tw-focus-ring",
                  )}
                >
                  {body}
                </Link>
              )}
            </li>
          );
        })}
      </ol>

      <Link
        to={parent.to}
        aria-label={`Back to ${parent.label}`}
        className={cn(
          STEP_CLASSES,
          "-tw-ml-1.5 tw-max-w-full tw-text-sm tw-font-medium tw-text-primary tw-no-underline",
          "hover:tw-bg-primary-soft hover:tw-text-primary hover:tw-no-underline tw-focus-ring min-[768px]:tw-hidden",
        )}
      >
        <ChevronLeft size={16} aria-hidden="true" className="tw-shrink-0" />
        <span className="tw-truncate">{parent.label}</span>
      </Link>
    </nav>
  );
}
